import { Box, Button, Group, Text } from '@mantine/core';
import { IconAlertOctagon, IconAlertTriangle, IconBrandWhatsapp } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { salesWhatsappLink } from '@constants/contact';
import { BILLING_ENABLED } from '@constants/features';
import { pageUrl } from '@constants/url';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import useSubscription from '@hooks/subscription/use-subscription';
import { deriveTrialStatus } from '@utils/trial-status';

/** WhatsApp brand green: contacting sales is a friendly action and must not
 *  inherit the strip's warning/danger color. Dark text keeps it readable. */
const WHATSAPP_GREEN = '#25D366';
const WHATSAPP_GREEN_HOVER = '#1EBE5A';
const WHATSAPP_TEXT = '#062E1B';

/**
 * Shell-level trial notice, mounted once above every dashboard page: a slim
 * status strip on the card surface with a colored edge, not an error slab.
 * Amber from three days before the trial cuts off, red on the last day and
 * once it has lapsed. Deliberately not dismissable. Hidden on the
 * subscription page, which carries the same message above the plan picker.
 */
export default function TrialBanner() {
  const { t } = useLanguage();
  const { colors: themeColors } = useTheme();
  const { pathname } = useLocation();
  const { data, loading, error } = useSubscription();

  if (loading || error || !data) return null;
  const trial = deriveTrialStatus(data);
  if (!trial.isTrial || (!trial.ended && !trial.endsSoon)) return null;
  if (pathname.startsWith(pageUrl.subscription)) return null;

  const endsToday = !trial.ended && trial.daysLeft === 0;
  const urgent = trial.ended || endsToday;
  const tone: string = urgent ? themeColors.error : themeColors.warning;
  const Icon = trial.ended ? IconAlertOctagon : IconAlertTriangle;

  const lead = trial.ended
    ? t('subscription.trial.strip.ended')
    : endsToday
      ? t('subscription.trial.strip.today')
      : t('subscription.trial.strip.soon').replace('{n}', String(trial.daysLeft));
  const detail = trial.ended ? t('subscription.trial.strip.endedDetail') : t('subscription.trial.strip.soonDetail');

  return (
    <Box
      role="status"
      data-testid="trial-banner"
      mb="lg"
      px="md"
      py="xs"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        minHeight: 48,
        // A faint wash of the tone fading into the card surface: enough to
        // read as "notice" at a glance, without the flat error-slab feel.
        background: `linear-gradient(90deg, ${tone}2E 0%, ${tone}12 38%, ${themeColors.card} 78%)`,
        border: `1px solid ${themeColors.border}`,
        borderLeft: `4px solid ${tone}`,
        borderRadius: 8,
      }}
    >
      <Icon size={18} stroke={1.75} style={{ color: tone, flexShrink: 0 }} aria-hidden />
      <Text size="sm" style={{ color: themeColors.text, flex: '1 1 320px', lineHeight: 1.45 }}>
        <Text component="span" fw={600} inherit>
          {lead}
        </Text>{' '}
        <Text component="span" inherit style={{ color: themeColors.textSecondary }}>
          {detail}
        </Text>
      </Text>
      <Group gap="xs" wrap="wrap" style={{ marginLeft: 'auto' }}>
        {/* Without the billing area /subscription redirects to the dashboard,
            so sales is the only real way forward and a plan link would mislead. */}
        {BILLING_ENABLED && (
          <Button component={Link} to={pageUrl.subscription} variant="subtle" size="xs" color="gray">
            {trial.ended ? t('subscription.banner.trial.cta') : t('subscription.trial.running.cta')}
          </Button>
        )}
        <Button
          component="a"
          href={salesWhatsappLink(t('subscription.trial.contactSales.message'))}
          target="_blank"
          rel="noopener noreferrer"
          size="xs"
          fw={600}
          leftSection={<IconBrandWhatsapp size={14} />}
          styles={{
            root: {
              backgroundColor: WHATSAPP_GREEN,
              color: WHATSAPP_TEXT,
              '&:hover': { backgroundColor: WHATSAPP_GREEN_HOVER },
            },
          }}
        >
          {t('subscription.trial.contactSales')}
        </Button>
      </Group>
    </Box>
  );
}
